import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part, type RibbonKey } from '../body';
import type { Hat } from '../headwear';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';

// --- The cap ---------------------------------------------------------------------------------------------------------

/** The view's squash (a level circle `r` across shows `r × SIN` deep; the conical hat's 6 in 28). */
const SIN = 0.21;
const COS = Math.sqrt(1 - SIN * SIN);
/** The light, toward it from the surface (picture x right, y up, z toward us): from the top-left, a little in front. */
const LIGHT = (() => {
  const v = [-0.75, 0.55, 0.4];
  const n = Math.hypot(...v);
  return v.map((x) => x / n) as [number, number, number];
})();

type V3 = [number, number, number];

/** The cap's make (art px, radians). */
interface CapShape {
  /** The rim's radius where it is highest (over the brow). */
  r: number;
  /** The spine's length from there to the tip. */
  len: number;
  /** How far it leans back, and how far more its tip flops back (from `from` along the spine on). */
  lean: number;
  bend: number;
  from: number;
  /** How much lower the rim hangs at the back of the head than over the brow (it covers the back of the head). */
  drop: number;
}
const CAP: CapShape = { r: 20, len: 22, lean: 0.08, bend: 1.7, from: 0.35, drop: 7 };
/** Which way the face looks round the cap's upright (0: right, π/2: toward us): the rim is highest there. */
const FACE_A = 0.75;

interface CapPose {
  shape?: Partial<CapShape>;
  /**
   * Turned in the picture's plane (positive: the tip toward the back), then round the upright (positive: its right side
   * away from us), then tipped (positive: its top toward us).
   */
  roll?: number;
  yaw?: number;
  pitch?: number;
}

/**
 * The cap as a 3D felt cone, in `pose`, drawn on a `w` × `h` canvas with the rim's middle at (ox, oy): the spine leans
 * back and its tip flops further, the rim hangs lower at the back of the head. Every pixel whose centre the cap covers
 * takes the nearest surface's tone, lit from the top-left in the team's ramp (`c b B`), the inside (seen through the rim
 * when it tumbles) in shadow.
 */
function capCanvas(pose: CapPose, w: number, h: number, ox: number, oy: number): PixelCanvas {
  const k = { ...CAP, ...pose.shape };
  const lo = -k.drop / k.len;
  const N = 70;
  const { yaw = 0, roll = 0, pitch = 0 } = pose;
  const [cr, sr, cy, sy, cp, sp] = [
    Math.cos(roll),
    Math.sin(roll),
    Math.cos(yaw),
    Math.sin(yaw),
    Math.cos(pitch),
    Math.sin(pitch),
  ];
  /** A direction turned by the pose: in the picture's plane, round the upright, then tipped. */
  const dir = (x: number, y: number, z: number): V3 => {
    const [x1, y1] = [x * cr - y * sr, x * sr + y * cr];
    const [x2, z2] = [x1 * cy + z * sy, -x1 * sy + z * cy];
    return [x2, y1 * cp - z2 * sp, y1 * sp + z2 * cp];
  };
  const pivot = k.len * 0.35;
  const Z = dir(0, 0, 1);
  // Rings along the spine, from the back of the rim (straight down from the brow's) to the tip: each its middle, its
  // radius (a felt cone, flaring a touch where it hangs below the brow's rim) and the way across it in the spine's plane.
  const rings: { u: number; r: number; at: V3; across: V3 }[] = [];
  let [sx, sy0] = [0, lo * k.len];
  for (let i = 0; i <= N; i++) {
    const u = lo + ((1 - lo) * i) / N;
    const t = k.lean + k.bend * Math.max(0, (u - k.from) / (1 - k.from)) ** 1.5;
    const at = dir(sx, sy0 - pivot, 0);
    at[1] += pivot;
    rings.push({
      u,
      r: u >= 0 ? k.r * (1 - u) ** 1.1 : k.r - u * k.len * 0.25,
      at,
      across: dir(Math.cos(t), Math.sin(t), 0),
    });
    sx -= (Math.sin(t) * k.len * (1 - lo)) / N;
    sy0 += (Math.cos(t) * k.len * (1 - lo)) / N;
  }
  const on = (ring: (typeof rings)[number], ca: number, sa: number): V3 => [
    ring.at[0] + ring.r * (ca * ring.across[0] + sa * Z[0]),
    ring.at[1] + ring.r * (ca * ring.across[1] + sa * Z[1]),
    ring.at[2] + ring.r * (ca * ring.across[2] + sa * Z[2]),
  ];
  const depth = new Float32Array(w * h).fill(-Infinity);
  const tone: (PaletteKey | null)[] = new Array(w * h).fill(null);
  for (let i = 0; i < N; i++) {
    const ring = rings[i]!;
    // Samples under half a pixel apart round the ring (and ring to ring), so every pixel centre has one near it.
    const A = Math.max(12, Math.ceil((2 * Math.PI * ring.r) / 0.45));
    for (let j = 0; j < A; j++) {
      const a = (j / A) * Math.PI * 2;
      // Where the rim is at this angle: highest over the brow, lowest at the back.
      const rim = lo * (1 - Math.cos(a - FACE_A)) * 0.5;
      if (ring.u < rim) continue;
      const [ca, sa] = [Math.cos(a), Math.sin(a)];
      const p = on(ring, ca, sa);
      const X = ox + p[0];
      const Y = oy - (p[1] * COS - p[2] * SIN);
      const px = Math.floor(X);
      const py = Math.floor(Y);
      if (px < 0 || py < 0 || px >= w || py >= h) continue;
      // Only a sample near the pixel's centre paints it: the edge is the cap's, not every pixel it grazes.
      if (Math.abs(X - px - 0.5) > 0.36 || Math.abs(Y - py - 0.5) > 0.36) continue;
      const d = p[1] * SIN + p[2] * COS;
      const idx = py * w + px;
      if (d <= depth[idx]!) continue;
      depth[idx] = d;
      // The surface's normal: across the two directions it runs in (along the spine, round the ring), outward.
      const q = on(rings[i + 1]!, ca, sa);
      const e1 = [q[0] - p[0], q[1] - p[1], q[2] - p[2]] as const;
      const out = [ca * ring.across[0] + sa * Z[0], ca * ring.across[1] + sa * Z[1], ca * ring.across[2] + sa * Z[2]];
      const e2 = [-sa * ring.across[0] + ca * Z[0], -sa * ring.across[1] + ca * Z[1], -sa * ring.across[2] + ca * Z[2]];
      let n: V3 = [e1[1] * e2[2]! - e1[2] * e2[1]!, e1[2] * e2[0]! - e1[0] * e2[2]!, e1[0] * e2[1]! - e1[1] * e2[0]!];
      if (n[0] * out[0]! + n[1] * out[1]! + n[2] * out[2]! < 0) n = [-n[0], -n[1], -n[2]];
      // Into the picture's frame: y up, z toward us.
      const cam: V3 = [n[0], n[1] * COS - n[2] * SIN, n[1] * SIN + n[2] * COS];
      const lit = (cam[0] * LIGHT[0] + cam[1] * LIGHT[1] + cam[2] * LIGHT[2]) / Math.hypot(...cam);
      const fromRim = (ring.u - rim) * k.len;
      if (cam[2] < 0) tone[idx] = fromRim < 2.5 ? 'B' : 'N';
      else {
        const t: PaletteKey = lit > 0.94 ? 'c' : lit > 0.3 ? 'b' : 'B';
        // The hem: its edge a step darker, where the felt turns under.
        tone[idx] = fromRim < 1.2 ? (t === 'c' ? 'b' : 'B') : t;
      }
    }
  }
  const c = new PixelCanvas(w, h);
  for (let i = 0; i < w * h; i++) if (tone[i]) c.pixels[i] = tone[i]!;
  return c;
}

/** The rim's middle over the brow, in the hat's canvas (its top at `HAT_Y`): the eyes clear under it. */
const ON_HEAD = [30, 21.5] as const;
const worn = (bend: number) => capCanvas({ shape: { bend } }, 64, 30, ...ON_HEAD);

/**
 * `c`'s pixels moved onto a 64 × 40 canvas (a knocked-off hat's frame), the middle of their box at `x` and its middle
 * (or, `ground`, its bottom) at `y`.
 */
function placed(c: PixelCanvas, x: number, y: number, ground = false): PixelCanvas {
  let [x0, y0, x1, y1] = [c.w, c.h, 0, 0];
  for (let yy = 0; yy < c.h; yy++) {
    for (let xx = 0; xx < c.w; xx++) {
      if (c.get(xx, yy) === null) continue;
      [x0, y0, x1, y1] = [Math.min(x0, xx), Math.min(y0, yy), Math.max(x1, xx + 1), Math.max(y1, yy + 1)];
    }
  }
  return new PixelCanvas(64, 40).blit(c, Math.round(x - (x0 + x1) / 2), Math.round(y - (ground ? y1 : (y0 + y1) / 2)));
}

/** Off the head the felt no longer stretches round it: narrower, its rim level, its tip less heavy. */
const OFF: Partial<CapShape> = { r: 15, len: 28, drop: 0, bend: 1.1 };
/** The cap off the head in `pose`, on a knocked-off hat's frame (`placed`). */
const loose = (pose: CapPose, y = 19, ground = false) =>
  placed(capCanvas({ ...pose, shape: { ...OFF, ...pose.shape } }, 96, 96, 48, 60), 32, y, ground);

const NOTHING: Part = [new PixelCanvas(1, 1), 0, 0];
const NO_RIBBON: Record<RibbonKey, Part> = {
  hang: NOTHING,
  hang2: NOTHING,
  flowA: NOTHING,
  flowB: NOTHING,
  flowC: NOTHING,
};

// --- The beard -------------------------------------------------------------------------------------------------------

type Tufts = readonly (readonly [x: number, y: number, r: number])[];

/** A beard's make (frame px): its outline as tufts, the moustache's two puffs over it, its volume and its point. */
interface BeardMake {
  tufts: Tufts;
  moustache: Tufts;
  /** The volume, lit from the top-left as one. */
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  /** Where its strands are combed to. */
  px: number;
  py: number;
}

/**
 * Standing: from the sideburns round the cheeks under the eyes, the mouth hidden, to a point at the belt (its rim
 * above the legs: the wardrobe's cap alone stops there).
 */
const STANDING: BeardMake = {
  tufts: [
    [34.5, 50.5, 3],
    [31, 48, 4],
    [38, 48, 4],
    [27, 46, 5],
    [33, 46, 5.5],
    [40, 45, 5],
    [45, 43.5, 3.5],
    [22, 37.5, 3],
    [23.5, 42, 4],
    [28.5, 44.5, 4.5],
    [46, 41.5, 3],
  ],
  moustache: [
    [34, 42.5, 4],
    [41, 42, 4],
  ],
  cx: 34,
  cy: 44.5,
  rx: 14,
  ry: 10.5,
  px: 35,
  py: 55,
};

/**
 * Lying on his back (the dead pose's head: the chin left, the eyes shut at x 36): round the jaw from ear to ear, over
 * the mouth, fanned on the chest to a point short of the trousers (the team still shows on them).
 */
const LYING: BeardMake = {
  tufts: [
    [31.5, 48.5, 2.5],
    [29.5, 51, 3.5],
    [29.5, 63, 3.5],
    [31.5, 65.5, 2.5],
    [27.5, 54, 4.5],
    [27.5, 60, 4.5],
    [24.5, 57, 4],
    [22.5, 57, 2.5],
  ],
  moustache: [
    [31.5, 55, 2.5],
    [31.5, 59.5, 2.5],
  ],
  cx: 28,
  cy: 56,
  rx: 9,
  ry: 11,
  px: 20,
  py: 57,
};

const DEEPER: Partial<Record<PaletteKey, PaletteKey>> = { w: '5', '5': '4', '4': '3' };
const within = (list: Tufts, x: number, y: number) =>
  list.findIndex(([cx, cy, r]) => (x - cx) ** 2 + (y - cy) ** 2 <= r * r);

/**
 * The gnome's beard: white (`w 5 4`, the slate `3` in its deepest grooves), lit as one volume, combed in strands to its
 * point; the moustache in two puffs over it, parted under the nose, its shadow under them.
 */
function beardPart(b: BeardMake): Part {
  const all = [...b.tufts, ...b.moustache];
  const x0 = Math.floor(Math.min(...all.map(([x, , r]) => x - r)));
  const y0 = Math.floor(Math.min(...all.map(([, y, r]) => y - r)));
  const c = new PixelCanvas(
    Math.ceil(Math.max(...all.map(([x, , r]) => x + r))) - x0,
    Math.ceil(Math.max(...all.map(([, y, r]) => y + r))) - y0,
  );
  // From the first puff toward the second, a pixel: where they meet is the parting.
  const [ax, ay] = b.moustache[0]!;
  const [bx, by] = b.moustache[1]!;
  const step = [Math.sign(Math.round((bx - ax) / 2)), Math.sign(Math.round((by - ay) / 2))] as const;
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [fx, fy] = [x + x0 + 0.5, y + y0 + 0.5];
      const m = within(b.moustache, fx, fy);
      if (m >= 0) {
        const [cx, cy, r] = b.moustache[m]!;
        const lit = -0.6 * ((fx - cx) / r) - 0.8 * ((fy - cy) / r);
        // The parting under the nose, where the two puffs meet.
        const parting = m === 0 && within(b.moustache.slice(1), fx + step[0], fy + step[1]) >= 0;
        c.set(x, y, parting ? '4' : lit > 0.15 ? 'w' : lit > -0.55 ? '5' : '4');
        continue;
      }
      if (within(b.tufts, fx, fy) < 0) continue;
      const whole = -0.55 * ((fx - b.cx) / b.rx) - 0.75 * ((fy - b.cy) / b.ry);
      let tone: PaletteKey = whole > 0.2 ? 'w' : whole > -0.5 ? '5' : '4';
      const d = Math.hypot(fx - b.px, fy - b.py);
      const phi = Math.atan2(fx - b.px, b.py - fy) + 0.05 * Math.sin(d * 0.6);
      if (d > 7 && tone !== '4' && (((phi / 0.22) % 1) + 1) % 1 < 0.2) tone = DEEPER[tone]!;
      // The moustache's shadow just under it.
      if (within(b.moustache, fx, fy - 1) >= 0) tone = '4';
      else if (within(b.moustache, fx, fy - 2) >= 0) tone = DEEPER[tone]!;
      c.set(x, y, tone);
    }
  }
  return [c, x0, y0];
}

const CAP_HAT = (): Hat => ({
  worn: false,
  hat: { flat: worn(CAP.bend), up: worn(CAP.bend - 0.2), down: worn(CAP.bend + 0.2) },
  ribbon: NO_RIBBON,
  head: [beardPart(STANDING)],
  lying: [beardPart(LYING)],
  fly: [loose({}), loose({ roll: -Math.PI / 2 }), loose({ roll: Math.PI, pitch: 0.6 }), loose({ roll: Math.PI / 2 })],
  // Sitting on the grass, its tip slumped.
  rest: [loose({ shape: { bend: 1.7 } }, 37, true)],
});

// --- The tunic -------------------------------------------------------------------------------------------------------

// A forest-green tunic (green keeps off the skin's and the team's keys), lit from the top-left: a V at the throat in the
// look's skin, sleeves seamed at the shoulder with a dark cuff, a black leather belt with a brass buckle, the skirt
// pleated over the hips, its hem in shadow.
const TUNIC = grid(`
  ....gggggggGGGGGGGGGSsssssSGGGDD....
  ..gglgggGGGGGGGGGGGGGSsssSGGGGGDDD..
  .glggggGGGGGGGGGGGGGGGSsSGGGGGGGDDD.
  glgggGGGDGGGGGGGGGGGGGGSGGGGGGGDGDDD
  gggGGGGDGGGGGGGGGGGGGGGGGGGGGGGDGDDD
  ggGGGGGDGGGGGGGGGGGGGGGGGGGGGGGDGDDD
  .DDDDDDgGGGGGGGGGGGGGGGGGGGGGGGDDDD.
  ....gGGGGGGGDGGGGGGGGGGGGGGGGGDD....
  ....2222222222222yYYYU222222222D....
  ....1111111111111Y212U111111111k....
  ....1111111111111Y111U111111111k....
  ...gGGGGGGGGGGGGGUUUUuGGGGGGGGGDD...
  ...gGGGGDGGGGGGGGGGGGGGGDGGGGGGDD...
  ..gGGGGGDGGGGGGGGGGGGGGGDGGGGGGGDD..
  ..DDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDD..
`);

// The tunic lying: its skirt over the hips (left; the team's trousers still show past it), the belt and buckle, the body
// under the head, a sleeve either side.
const DEAD_TUNIC = grid(`
  ........gggg
  .......ggGGG
  .g21gGGGGGGG
  gG21GGGGGGGG
  gG21GGGGGGGG
  gG21GGGGGGGG
  gGyUGGGGGGGG
  gGY2GGGGGGGG
  gGY1GGGGGGGG
  gGUuGGGGGGGG
  gG21GGGGGGGG
  gG21GGGGGGGG
  gG21GGGGGGGG
  gG21GGGGGGGG
  .D21GGGGGGG.
  ..21DDGGGGGG
  .......DDDDD
  ........DDDD
`);

const TUNIC_OUTFIT: Outfit = { torso: [TUNIC, 12, 44], dead: [DEAD_TUNIC, 20, 51] };

// --- The gun ---------------------------------------------------------------------------------------------------------

/** Toadstool: the steel a fly agaric's red cap, the wood and brass its cream stem and spots. */
const TOADSTOOL = {
  k: 'N',
  '1': 'N',
  '2': 'R',
  '3': 'r',
  '4': 'i',
  '5': 'e',
  m: 't',
  t: 'e',
  e: 'w',
  U: 't',
  Y: 'e',
  y: 'w',
} as const satisfies Partial<Record<PaletteKey, PaletteKey>>;

/**
 * The gnome set (ADR-0112): a garden gnome's tall felt cap in the team's colour with a big white beard, a belted
 * forest-green tunic, and the Toadstool finish on the guns.
 *
 * The cap is a felt cone modelled in 3D and drawn in the game's 3/4 view (`capCanvas`), so the cap worn, tumbling and
 * sitting on the grass agree. Worn, its rim hangs lower at the back of the head (it covers the hair there) and is
 * highest over the brow (the eyes clear under it); its spine leans back and the tip flops back further, more or less
 * as the hat lags the head (the brim's flex). It is all in the team's ramp (`c b B`), so the team reads on the biggest
 * thing a gardener wears; it has no ribbon. Off the head it is narrower (the felt no longer stretched over a head): it
 * spins a turn as it flies, showing its hollow upside down, and sits on the grass with its tip slumped.
 * The beard comes with the cap (`head`, drawn with the face under the cap: the mouth and any beard hidden, the eyes
 * between the two) and stays on the gnome when the cap flies off (`lying`).
 *
 * Rubric (the looks sheets at 3× and 1:1 on every team, idle, run and dead; the cap's own frames; the wardrobe tiles; a
 * 2v2 of gnomes at 1280×720 and 1920×1080): cap S5 R5 P4 L4 C4 A4 F4, tunic S4 R4 P5 L4 C4 A4 F4, Toadstool S5 R5 P5
 * L4 C5 F4. Still weak: the tip's curl leaves a 1-px notch and the cap's light bands step at 8×; the tumble's sideways frames are mostly in shadow; the lying beard
 * reads as a white wedge more than a beard at 1:1; the tunic's green is close to the lawn's at 1:1 (its belt and the
 * outline carry it); on the run's highest frame the hat's lag lets the hair peek over the cap's back.
 */
export const GNOME = drawnLater({ head: CAP_HAT, outfit: () => TUNIC_OUTFIT, gun: TOADSTOOL });
